/**
 * Capabilities EventType на основе OutcomeScheme — аналог eventCategoryCapabilities,
 * но работающий с EventType вместо жёстко заданных EventCategory.
 *
 * Определяет допустимые исходы, применимые scoring-правила,
 * параметры формы ставки/результата и генерацию исходов из конфигурации EventType.
 *
 * На этапе миграции сосуществует с eventCategoryCapabilities.js.
 */

import { OutcomeScheme, ScoringRuleType, DEFAULT_WIN_METHODS } from '../../../models/eventType';
import { DrawPolicy, OutcomeCode, outcomeCodeShortLabel } from '../scoring/mainOutcomeHelpers';

// ===================================================================================================================
// = Генерация исходов по OutcomeScheme
// ===================================================================================================================

/**
 * Возвращает список строк-исходов для EventType на основе OutcomeScheme и конфигурации.
 * Строки идентичны OutcomeCode и попадают в Event.outcomes.
 */
export function generateOutcomes(eventType) {
  if (!eventType) return [];

  const scheme = eventType.outcomeScheme;

  switch (scheme) {
    case OutcomeScheme.SCORE_BASED:
      return generateScoreBasedOutcomes(eventType.scoreDrawPolicy);

    case OutcomeScheme.WINNER_ONLY:
      return generateWinnerOnlyOutcomes(eventType.participantCountMax);

    case OutcomeScheme.WINNER_WITH_METHOD:
      return generateWinnerWithMethodOutcomes(eventType.participantCountMax, eventType.winMethods);

    case OutcomeScheme.PLACEMENT:
      return [OutcomeCode.FIRST, OutcomeCode.SECOND, OutcomeCode.THIRD];

    case OutcomeScheme.INDIVIDUAL_SCORE:
      return [OutcomeCode.FIRST, OutcomeCode.SECOND, OutcomeCode.THIRD];

    case OutcomeScheme.CUSTOM:
    default:
      return [];
  }
}

// ===================================================================================================================
// = Score-based исходы
// ===================================================================================================================

function generateScoreBasedOutcomes(drawPolicy) {
  switch (drawPolicy) {
    case DrawPolicy.DRAW_ALLOWED:
      return [OutcomeCode.P1, OutcomeCode.X, OutcomeCode.P2];
    case DrawPolicy.DRAW_FORBIDDEN:
      return [OutcomeCode.P1, OutcomeCode.P2];
    case DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER:
      return [OutcomeCode.P1, OutcomeCode.P2, OutcomeCode.X1, OutcomeCode.X2];
    default:
      return [OutcomeCode.P1, OutcomeCode.X, OutcomeCode.P2];
  }
}

// ===================================================================================================================
// = Winner-only исходы
// ===================================================================================================================

function generateWinnerOnlyOutcomes(participantCountMax) {
  const count = Math.max(2, participantCountMax || 2);
  const outcomes = [];
  for (let i = 1; i <= count; i++) {
    outcomes.push(`P${i}`);
  }
  return outcomes;
}

// ===================================================================================================================
// = Winner-with-method исходы
// ===================================================================================================================

function generateWinnerWithMethodOutcomes(participantCountMax, winMethods) {
  const count = Math.max(2, participantCountMax || 2);
  const methods = winMethods?.length ? winMethods : [...DEFAULT_WIN_METHODS];
  const outcomes = [];
  for (let i = 1; i <= count; i++) {
    for (const method of methods) {
      outcomes.push(`P${i}_${method}`);
    }
  }
  return outcomes;
}

// ===================================================================================================================
// = Применимые scoring-правила
// ===================================================================================================================

const SCHEME_SCORING_RULES = Object.freeze({
  [OutcomeScheme.SCORE_BASED]: [
    ScoringRuleType.EXACT_SCORE,
    ScoringRuleType.SCORE_DIFFERENCE,
    ScoringRuleType.CORRECT_WINNER,
  ],
  [OutcomeScheme.WINNER_ONLY]: [
    ScoringRuleType.CORRECT_WINNER,
  ],
  [OutcomeScheme.WINNER_WITH_METHOD]: [
    ScoringRuleType.CORRECT_WINNER,
    ScoringRuleType.CORRECT_METHOD,
  ],
  [OutcomeScheme.PLACEMENT]: [
    ScoringRuleType.EXACT_PLACEMENT,
    ScoringRuleType.CORRECT_WINNER,
  ],
  [OutcomeScheme.INDIVIDUAL_SCORE]: [
    ScoringRuleType.EXACT_PLACEMENT,
    ScoringRuleType.CORRECT_WINNER,
  ],
  [OutcomeScheme.CUSTOM]: [
    ScoringRuleType.EXACT_MATCH,
    ScoringRuleType.PARTIAL_MATCH,
  ],
});

/**
 * Возвращает список ScoringRuleType, применимых для заданной OutcomeScheme.
 */
export function getApplicableScoringRules(scheme) {
  return SCHEME_SCORING_RULES[scheme] || [];
}

/**
 * Возвращает список опций scoring-правил для select-а/чекбоксов.
 */
export function getApplicableScoringRuleOptions(scheme) {
  const rules = getApplicableScoringRules(scheme);
  return rules.map((rule) => ({ value: rule, label: scoringRuleLabel(rule) }));
}

// ===================================================================================================================
// = DrawPolicy по схеме
// ===================================================================================================================

/**
 * Возвращает дефолтную DrawPolicy для OutcomeScheme.
 * Только SCORE_BASED использует DrawPolicy; остальные — null.
 */
export function getDefaultDrawPolicyForScheme(scheme) {
  if (scheme === OutcomeScheme.SCORE_BASED) {
    return DrawPolicy.DRAW_ALLOWED;
  }
  return null;
}

/**
 * Возвращает опции DrawPolicy для формы EventType.
 */
export function getDrawPolicyOptions() {
  return [
    { value: DrawPolicy.DRAW_ALLOWED, label: 'Ничья допустима (P1 / X / P2)' },
    { value: DrawPolicy.DRAW_FORBIDDEN, label: 'Без ничьей (P1 / P2)' },
    { value: DrawPolicy.DRAW_WITH_TIE_BREAK_WINNER, label: 'Ничья с тай-брейком (P1 / P2 / X1 / X2)' },
  ];
}

// ===================================================================================================================
// = Форматирование
// ===================================================================================================================

/**
 * Форматированная строка с допустимыми исходами для EventType.
 * Пример: "P1 / X / P2" или "P1_KO / P1_SUB / P1_DECISION / P2_KO / ..."
 */
export function formatAllowedOutcomes(eventType) {
  return generateOutcomes(eventType)
    .map(outcomeCodeShortLabel)
    .join(' / ');
}

/**
 * Возвращает описание формы ставки для OutcomeScheme.
 */
export function getBetFormDescription(scheme) {
  switch (scheme) {
    case OutcomeScheme.SCORE_BASED:
      return 'Укажи счёт и код исхода (P1/X/P2)';
    case OutcomeScheme.WINNER_ONLY:
      return 'Выбери победителя из списка участников';
    case OutcomeScheme.WINNER_WITH_METHOD:
      return 'Выбери победителя и способ победы';
    case OutcomeScheme.PLACEMENT:
      return 'Укажи распределение мест';
    case OutcomeScheme.INDIVIDUAL_SCORE:
      return 'Укажи место и балльную оценку';
    case OutcomeScheme.CUSTOM:
      return 'Укажи произвольный текст исхода';
    default:
      return '';
  }
}

// ===================================================================================================================
// = Implementation
// ===================================================================================================================

const SCORING_RULE_LABELS = Object.freeze({
  [ScoringRuleType.EXACT_SCORE]: 'Точный счёт',
  [ScoringRuleType.SCORE_DIFFERENCE]: 'Точная разница',
  [ScoringRuleType.CORRECT_WINNER]: 'Верный победитель',
  [ScoringRuleType.CORRECT_METHOD]: 'Верный способ победы',
  [ScoringRuleType.EXACT_PLACEMENT]: 'Точное место',
  [ScoringRuleType.EXACT_MATCH]: 'Точное совпадение (legacy)',
  [ScoringRuleType.PARTIAL_MATCH]: 'Частичное совпадение (legacy)',
});

function scoringRuleLabel(ruleType) {
  return SCORING_RULE_LABELS[ruleType] || ruleType;
}
